import React, { useState, useEffect, useRef } from "react";
import { NavLink as Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { saveAs } from "file-saver";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Footer from "../../common/components/Footer";
import Nav from "../../common/components/Nav";
import { storeSearchParams } from "../../store/actions/actions/survey";
import WeatherSlider from "../../common/components/WeatherSlider";
import { fetchPublicPages, fetchPublicPagesByType } from "../../utils/api/cms/page";
import { errorHandler } from "../../utils/helpers/errorHandler";
import { fetchAllFuels } from "../../utils/api/liveFeedApi";
import { setFuelData } from "../../store/actions/liveFeedActions";
import useSharedFuelData from '../../hooks/useSharedFuelData';
import { Sparklines, SparklinesBars, SparklinesLine, SparklinesSpots } from 'react-sparklines';
import { parseBodyToChartData } from "../Budget/BudgetPage";

const SPARKLINE_COLORS = [
  '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8',
  '#6f42c1', '#e83e8c', '#fd7e14', '#20c997', '#6610f2',
  '#0dcaf0', '#198754', '#ff6b6b', '#4ecdc4', '#ffd166',
  '#06d6a0', '#118ab2', '#ef476f', '#073b4c', '#7209b7'
];

/**
 * Reusable zero-dependency scroll animation wrapper using IntersectionObserver
 */
const ScrollReveal = ({ children, className = "", delayClass = "" }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.15 }
    );

    const currentRef = domRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`reveal-section ${isVisible ? "is-visible" : ""} ${delayClass} ${className}`}
    >
      {children}
    </div>
  );
};

const token = JSON.parse(localStorage.getItem("eko-360-p2-token"))?.token;
const rootUrl = `${process.env.REACT_APP_ROOT_URL}`;

const HomePage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { fuelData } = useSelector(
    (state) => state.liveFeed
  );

  const [query, setQuery] = useState("");
  const [publicPages, setPublicPages] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [analysis, setAnalysis] = useState([]);
  const [budgets, setBudget] = useState([]);
  const [quickReads, setQuickReads] = useState([]);
  
  const [publicDatasets, setPublicDatasets] = useState([]);
  const [publicAnalysis, setPublicAnalysis] = useState([]);
  const [publicBudgets, setPublicBudget] = useState([]);
  const [publicQuickReads, setPublicQuickReads] = useState([]);

  // Active selected fuel item for 2-column detail view
  const [selectedFuelId, setSelectedFuelId] = useState(null);

  const budgetYear = localStorage.getItem('budgetYear') || new Date().getFullYear();
  const budgetText = localStorage.getItem('budgetText') || "Lagos State Comprehensive Budget";
  const yearLabel = localStorage.getItem('budgetYearLabel') || "Year";

  const { sharedFuelData } = useSharedFuelData();

  const getColorForFuel = (fuelType, index) => {
    const existingFuel = fuelData.find(f => f.fuelType === fuelType);
    if (existingFuel) {
      return existingFuel.sparklineColor;
    }
    return SPARKLINE_COLORS[index % SPARKLINE_COLORS.length];
  };

  const handleSearch = (event) => {
    event.preventDefault();
    if (!query) {
      return toast.error("The search word is required");
    }
    dispatch(storeSearchParams({ query }));
    history.push("/search-dataset");
  };

  const handleFetchPublicPages = async (page = 0) => {
    try {
      const data = await fetchPublicPages(page);
      setPublicPages(data.data.allPages);
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  const handleFetchPublicDatasets = async (page = 0) => {
    try {
      const data = await fetchPublicPagesByType(page, 'datasets');
      setPublicDatasets(data.data.allPages);
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  const handleFetchPublicQuickReads = async (page = 0) => {
    try {
      const data = await fetchPublicPagesByType(page, 'quick_reads');
      setPublicQuickReads(data.data.allPages);
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  const handleFetchPublicBudgets = async (page = 0) => {
    try {
      const data = await fetchPublicPagesByType(page, 'budgets');
      setPublicBudget(data.data.allPages);
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  const handleFetchPublicAnalysis = async (page = 0) => {
    try {
      const data = await fetchPublicPagesByType(page, 'analysis');
      setPublicAnalysis(data.data.allPages);
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  useEffect(() => {
    handleFetchPublicPages();
    handleFetchPublicDatasets();
    handleFetchPublicQuickReads();
    handleFetchPublicBudgets();
    handleFetchPublicAnalysis();
  }, []);

  useEffect(() => {
    if (publicDatasets && publicAnalysis && publicBudgets && publicQuickReads) {
      const datasets = publicDatasets.filter((pages) => pages.type === "datasets");
      const quickReads = publicQuickReads.filter((pages) => pages.type === "quick_reads");
      const budgets = publicBudgets.filter((pages) => pages.type === "budgets");
      const analysis = publicAnalysis.filter((pages) => pages.type === "analysis");

      budgets
        .filter((budget) => /water/i.test(budget.title || ""))
        .forEach((budget) =>
          console.log("Waterways budget entry raw fields:", {
            title: budget.title,
            body: budget.body,
            budget: budget.budget,
          })
        );

      const latestBudgets = [...budgets]
        .sort((a, b) => {
          const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime() || 0;
          const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime() || 0;
          return bTime - aTime;
        })
        .slice(0, 4);

      setDatasets(datasets);
      setQuickReads(quickReads.slice(0, 3));
      setBudget(latestBudgets);
      setAnalysis(analysis.slice(0, 3));
    }
  }, [publicDatasets, publicAnalysis, publicBudgets, publicQuickReads]);

  const handleFileDownload = async (event, dataset) => {
    event.preventDefault();
    try {
      const fileName = dataset.file.substr(dataset.file.lastIndexOf("/") + 1);
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/datasets/${fileName}/download/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );
      const blob = await res.blob();
      saveAs(blob, dataset.title);
    } catch (error) {
      return toast.error(`An unexpected error just occurred: ${error.message}`);
    }
  };

  useEffect(() => {
    const loadFuels = async () => {
      try {
        const fuels = await fetchAllFuels();
        const transformedFuels = fuels.map((fuel, index) => {
          const currentPrice = typeof fuel.price === 'string' && fuel.price.startsWith('$') 
            ? parseFloat(fuel.price.substring(1)) 
            : parseFloat(fuel.price) || 0;
          
          const sparklineColor = getColorForFuel(fuel.type, index);
          
          return {
            id: fuel.id,
            uuid: fuel.uuid,
            fuelType: fuel.type,
            price: currentPrice,
            link: fuel.link,
            tooltipData: fuel.data,
            sparklineData: Array.from({length: 24}, () => 
              Math.random() * 50 + currentPrice - 25
            ),
            sparklineColor: sparklineColor
          };
        });
        
        dispatch(setFuelData(transformedFuels));
        
        if (transformedFuels.length > 0 && !selectedFuelId) {
          setSelectedFuelId(transformedFuels[0].id);
        }
      } catch (err) {
        console.error("Error fetching fuels:", err);
      }
    };
    
    loadFuels();
  }, [dispatch]);

  const activeFuel = fuelData.find((f) => f.id === selectedFuelId) || fuelData[0];

  return (
    <div className="d-flex flex-column min-vh-100 bg-white" style={{ fontFamily: "Inter, system-ui, -apple-system, sans-serif" }}>
      
      {/* 1. Header Navigation */}
      <header className="sticky-top bg-white z-3">
        <Nav />
      </header>

      {/* 2. Hero Section */}
      <ScrollReveal>
        <section
          className="text-white position-relative overflow-hidden pt-5 pb-lg-5"
          style={{
            backgroundColor: "#0B1320",
            backgroundImage: `url('/assets/img/lagos_background.jpg')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat"
          }}
        >
          <div className="container pt-lg-5 pb-3 position-relative" style={{ zIndex: 2 }}>
            <div className="row align-items-start g-5">
              <div className="col-lg-7 text-start">
                <h1 className="display-4 fw-bold lh-sm mb-3">
                  Lagos Bureau of Statistics
                  <br />
                  <span style={{ color: "#0C9EA9" }}>Data Intelligence</span>
                </h1>
                <p className="text-white fw-light mb-4" style={{ fontSize: "14px" }}>
                  Actionable Data &amp; Predictive Models for Lagos State
                </p>

                <form onSubmit={handleSearch} className="d-flex flex-column flex-sm-row gap-2 max-w-lg mb-4">
                  <select
                    required
                    className="form-select border-0 p-3 shadow-none text-dark fw-medium rounded-3"
                    style={{ fontSize: "15px" }}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  >
                    <option value="" disabled>Select Sector Category...</option>
                    <option value="Education">Education & Human Capital</option>
                    <option value="Health">Healthcare Insights</option>
                    <option value="Economic Affairs">Economic Affairs & Trade</option>
                    <option value="public order safety">Public Safety & Order</option>
                    <option value="general public service">General Public Service</option>
                    <option value="environmental protection">Environmental Protection</option>
                    <option value="housing and community amenities">Housing & Urban Development</option>
                    <option value="social protection">Social Welfare</option>
                    <option value="recreation culture and religion">Recreation & Culture</option>
                  </select>
                  <button
                    type="submit"
                    className="btn text-white fw-bold px-4 py-3 rounded-3 text-nowrap"
                    style={{ backgroundColor: "#0C9EA9", border: "none" }}
                  >
                    Explore Data ↗
                  </button>
                </form>

                <p className="text-white-20 small mb-0">
                  Making forward-looking data accessible for government agencies,
                  institutional investors, and public decision-makers.
                </p>

              </div>

              <div className="col-lg-5">
                <div className="p-4 rounded-4 shadow-lg" style={{ backgroundColor: "#131F33", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <h5 className="fw-bold text-white mb-2 d-flex align-items-center justify-content-between">
                    <span>Economic Watch</span>
                    <span className="badge bg-success bg-opacity-25 text-white fs-6 fw-normal">Live Tracking</span>
                  </h5>
                  <p className="text-white small mb-4">Real-time surveillance on fuel prices and economic indicators.</p>
                  
                  <div className="d-flex flex-column gap-3">
                    {fuelData.slice(0, 3).map((fuel, i) => (
                      <div 
                        key={i} 
                        className="p-3 rounded-3 d-flex align-items-center justify-content-between cursor-pointer hover-shift" 
                        style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
                        onClick={() => setSelectedFuelId(fuel.id)}
                      >
                        <div>
                          <span className="text-white d-block small fw-semibold">{fuel.fuelType}</span>
                          <h5 className="fw-bold text-white m-0">₦{fuel.price.toFixed(2)}</h5>
                        </div>
                        <div style={{ width: "85px", height: "35px" }}>
                          <Sparklines data={fuel.sparklineData} width={85} height={35} margin={2}>
                            <SparklinesBars style={{ fill: fuel.sparklineColor }} />
                          </Sparklines>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* 3. Core Pillars Section */}
      <ScrollReveal>
        <section className="py-5 bg-light">
          <div className="container py-2">
            <div className="text-center mb-5">
              <h6 className="text-uppercase fw-bold" style={{ color: "#0C9EA9", letterSpacing: "1.5px", fontSize: "12px" }}>
                Core Data Services
              </h6>
              <h2 className="fw-bold text-dark">Lagos Open Data Solutions</h2>
            </div>

            <div className="row g-4">
              <div className="col-lg-4 col-md-6">
                <ScrollReveal delayClass="reveal-delay-1">
                  <div className="card h-100 border-0 shadow-sm p-4 rounded-4 bg-white hover-lift">
                    <div className="mb-3 fs-3" style={{ color: "#0C9EA9" }}>📊</div>
                    <h5 className="fw-bold text-dark">Standardized Datasets</h5>
                    <p className="text-black small mb-4">
                      Download raw, verified data files across education, public safety, health, and urban infrastructure.
                    </p>
                    <Link to="/public/datasets" className="mt-auto fw-bold text-decoration-none small" style={{ color: "#0C9EA9" }}>
                      Browse Datasets ↗
                    </Link>
                  </div>
                </ScrollReveal>
              </div>

              <div className="col-lg-4 col-md-6">
                <ScrollReveal delayClass="reveal-delay-2">
                  <div className="card h-100 border-0 shadow-sm p-4 rounded-4 bg-white hover-lift">
                    <div className="mb-3 fs-3" style={{ color: "#0C9EA9" }}>📈</div>
                    <h5 className="fw-bold text-dark">Visual Analytics</h5>
                    <p className="text-black small mb-4">
                      Interactive dashboards that slice-and-dice multi-sector metrics and historical budget performance.
                    </p>
                    <Link to="/interactivity" className="mt-auto fw-bold text-decoration-none small" style={{ color: "#0C9EA9" }}>
                      Launch Dashboards ↗
                    </Link>
                  </div>
                </ScrollReveal>
              </div>

              <div className="col-lg-4 col-md-6">
                <ScrollReveal delayClass="reveal-delay-3">
                  <div className="card h-100 border-0 shadow-sm p-4 rounded-4 bg-white hover-lift">
                    <div className="mb-3 fs-3" style={{ color: "#0C9EA9" }}>📁</div>
                    <h5 className="fw-bold text-dark">Policy Briefs & Publications</h5>
                    <p className="text-black small mb-4">
                      In-depth macroeconomic analysis and policy summaries authored by state statistical teams.
                    </p>
                    <Link to="/publications" className="mt-auto fw-bold text-decoration-none small" style={{ color: "#0C9EA9" }}>
                      Read Publications ↗
                    </Link>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* 4. Two-Column Live Commodity & Energy Tracker */}
      <ScrollReveal>
        <section className="py-5 bg-white border-top border-bottom">
          <div className="container py-2">
            {/* Section Header */}
            <div className="d-flex flex-wrap align-items-end justify-content-between mb-4">
              <div>
                <h6
                  className="text-uppercase fw-bold text-muted mb-1"
                  style={{ letterSpacing: "1.2px", fontSize: "11px" }}
                >
                  Real-Time Surveillance
                </h6>
                <h2 className="fw-bold text-dark m-0">Live Commodity &amp; Energy Tracker</h2>
              </div>
              <span className="badge bg-light text-secondary border px-3 py-2 rounded-pill small fw-normal">
                ● Click item to load live chart
              </span>
            </div>

            <div className="row g-4">
              {/* Left Column: Commodity List */}
              <div className="col-lg-5 col-md-6">
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-light">
                  <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center">
                    <span
                      className="fw-bold text-muted small text-uppercase"
                      style={{ letterSpacing: "0.5px", fontSize: "11px" }}
                    >
                      Commodity List
                    </span>
                    <span className="text-muted small">{fuelData.length} Items</span>
                  </div>

                  <div style={{ maxHeight: "460px", overflowY: "auto" }}>
                    {fuelData.map((fuel) => {
                      const isSelected = activeFuel?.id === fuel.id;
                      return (
                        <div
                          key={fuel.id}
                          onClick={() => setSelectedFuelId(fuel.id)}
                          className={`p-3 border-bottom cursor-pointer hover-shift transition-all ${
                            isSelected
                              ? "bg-white border-start border-4 border-info shadow-sm"
                              : "bg-transparent"
                          }`}
                          style={{
                            cursor: "pointer",
                            borderColor: isSelected ? "#0C9EA9" : "#f1f3f5",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className="d-inline-block rounded-1"
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  backgroundColor: fuel.sparklineColor || "#0C9EA9",
                                }}
                              ></span>
                              <div>
                                <h6
                                  className={`mb-0 fw-bold ${
                                    isSelected ? "text-info" : "text-dark"
                                  }`}
                                  style={{ fontSize: "14px" }}
                                >
                                  {fuel.fuelType}
                                </h6>
                                <span
                                  className="text-muted small"
                                  style={{ fontSize: "11px" }}
                                >
                                  Tap to inspect &rarr;
                                </span>
                              </div>
                            </div>
                            <div className="text-end">
                              <div className="fw-bold text-dark fs-6">
                                &#8358;{fuel.price.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Column: Detailed Chart Viewer */}
              <div className="col-lg-7 col-md-6">
                {activeFuel ? (
                  <div className="card border-0 shadow-sm rounded-4 p-4 bg-white h-100 d-flex flex-column justify-content-between">
                    <div>
                      {/* Header Info */}
                      <div className="d-flex flex-wrap justify-content-between align-items-start border-bottom pb-3 mb-4">
                        <div>
                          <span
                            className="text-muted text-uppercase fw-semibold"
                            style={{ fontSize: "11px", letterSpacing: "1px" }}
                          >
                            Selected Commodity
                          </span>
                          <h3 className="fw-bold text-dark m-0">{activeFuel.fuelType}</h3>
                        </div>
                        <div className="text-end">
                          <span className="text-muted small d-block">Current Spot Price</span>
                          <h2 className="fw-bold m-0" style={{ color: "#0C9EA9" }}>
                            &#8358;{activeFuel.price.toFixed(2)}
                          </h2>
                        </div>
                      </div>

                      {/* Line Chart Area */}
                      <div className="mb-4 bg-light p-4 rounded-4 position-relative">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="fw-semibold text-secondary small">
                            Trend Visualizer
                          </span>
                          <span className="badge bg-dark text-white fw-medium">
                            24-Period Moving Window
                          </span>
                        </div>

                        <div style={{ width: "100%", height: "200px" }}>
                          <Sparklines
                            data={activeFuel.sparklineData}
                            width={300}
                            height={120}
                            margin={6}
                          >
                            <SparklinesLine
                              style={{
                                stroke: activeFuel.sparklineColor || "#007bff",
                                strokeWidth: 4,
                                fill: "none",
                              }}
                            />
                            <SparklinesSpots
                              size={4}
                              style={{ fill: activeFuel.sparklineColor || "#007bff" }}
                            />
                          </Sparklines>
                        </div>
                      </div>

                      {/* Bottom Metric Cards */}
                      <div className="row g-3 text-center">
                        <div className="col-4">
                          <div className="p-3 bg-light rounded-3 border-0">
                            <span className="text-muted small d-block mb-1">Min Range</span>
                            <strong className="text-dark">
                              &#8358;
                              {Math.min(...activeFuel.sparklineData).toFixed(2)}
                            </strong>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="p-3 bg-light rounded-3 border-0">
                            <span className="text-muted small d-block mb-1">Max Range</span>
                            <strong className="text-dark">
                              &#8358;
                              {Math.max(...activeFuel.sparklineData).toFixed(2)}
                            </strong>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="p-3 bg-light rounded-3 border-0">
                            <span className="text-muted small d-block mb-1">Status</span>
                            <strong className="text-success">Active Feed</strong>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* External Source Link */}
                    {activeFuel.link && (
                      <div className="pt-4 border-top mt-4 text-end">
                        <a
                          href={activeFuel.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-dark btn-sm rounded-2 px-3 fw-semibold"
                        >
                          Source Analytics Link &#8599;
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="card border-0 shadow-sm rounded-4 p-5 text-center text-muted bg-light h-100 d-flex align-items-center justify-content-center">
                    <p className="m-0">Select any commodity on the left to display its live chart.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* 5. Datasets Section */}
     {datasets.length > 0 && (
  <ScrollReveal>
    <section className="bg-light py-5">
      <div className="container py-2">
        <div className="d-flex flex-wrap align-items-end justify-content-between mb-4">
          <div>
            <h6 className="text-uppercase fw-bold" style={{ color: "#0C9EA9", letterSpacing: "1.2px", fontSize: "12px" }}>
              Public Repositories
            </h6>
            <h3 className="fw-bold text-dark m-0">Latest Open Datasets</h3>
          </div>
          <Link to="/datasetspage" className="btn btn-outline-dark btn-sm rounded-pill px-4 fw-semibold mt-2 mt-sm-0">
            View All Datasets ↗
          </Link>
        </div>

        <div className="row g-4 mb-2">
          {datasets.slice(0, 6).map((dataset, idx) => (
            <div className="col-xl-4 col-md-6 d-flex" key={idx}>
              <div className="h-100 w-100">
                <ScrollReveal className="h-100 w-100">
                  <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white d-flex flex-column hover-lift">
                    <div
                      style={{
                        height: "160px",
                        backgroundImage: `url(${rootUrl}/uploads/${dataset?.banner})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center"
                      }}
                    />
                    <div className="card-body p-4 d-flex flex-column">
                      <h5 className="card-title fw-bold text-dark mb-2 fs-6">
                        {dataset.title}
                      </h5>
                      <div
                        className="card-text text-black small flex-grow-1 mb-3"
                        dangerouslySetInnerHTML={{
                          __html: dataset.body,
                        }}
                      />
                      {dataset.file && (
                        <button
                          onClick={(e) => handleFileDownload(e, dataset)}
                          className="btn btn-sm text-white rounded-2 w-100 fw-semibold mt-auto"
                          style={{ backgroundColor: "#0C9EA9", border: "none" }}
                        >
                          <i className="fa fa-download me-2"></i> Download Dataset
                        </button>
                      )}
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </ScrollReveal>
)}

      {/* 6. Dashboards Section */}
    {analysis.length > 0 && (
  <ScrollReveal>
    <section className="py-5 bg-white border-top">
      <div className="container py-2">
        <div className="d-flex flex-wrap align-items-end justify-content-between mb-4">
          <div>
            <h6 className="text-uppercase fw-bold" style={{ color: "#0C9EA9", letterSpacing: "1.2px", fontSize: "12px" }}>
              Visual Analytics
            </h6>
            <h3 className="fw-bold text-dark m-0">Interactive Dashboards</h3>
          </div>
          <Link to="/analysispage" className="btn btn-outline-dark btn-sm rounded-pill px-4 fw-semibold mt-2 mt-sm-0">
            View All Dashboards ↗
          </Link>
        </div>

        <div className="row g-4 mb-2">
          {analysis.slice(0, 6).map((data, idx) => (
            <div className="col-xl-4 col-md-6 d-flex" key={idx}>
              <ScrollReveal className="h-100 w-100">
                <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden d-flex flex-column bg-light hover-lift">
                  <div
                    style={{
                      height: "170px",
                      backgroundImage: `url(${rootUrl}/uploads/${data?.banner})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    }}
                  />
                  <div className="card-body p-4 d-flex flex-column">
                    <h5 className="card-title fw-bold text-dark mb-2 fs-6">{data.title}</h5>
                    <div
                      className="card-text text-black small flex-grow-1 mb-3"
                      dangerouslySetInnerHTML={{
                        __html: data.body,
                      }}
                    />
                    {data.iFrame && (
                      <a
                        href={data.iFrame}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-dark text-white btn-sm rounded-2 w-100 fw-semibold mt-auto"
                      >
                        Launch Dashboard ↗
                      </a>
                    )}
                  </div>
                </div>
              </ScrollReveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  </ScrollReveal>
)}

      {/* 7. Budget Section */}
      {budgets.length > 0 && (
        <ScrollReveal>
          <section className="py-5 bg-light border-top">
            <div className="container py-2">
              <div className="text-center mb-5">
                <h6 className="text-uppercase fw-bold" style={{ color: "#0C9EA9", letterSpacing: "1.2px", fontSize: "12px" }}>
                  Fiscal Transparency
                </h6>
                <h3 className="fw-bold text-dark mb-2">
                  {yearLabel} {budgetYear} {budgetText}
                </h3>
              </div>

              <div className="row g-4 mb-4">
                {budgets.slice(0, 6).map((budget, idx) => {
                  const budgetSource =
                    budget.budget && budget.budget.trim()
                      ? budget.budget
                      : budget.body || "";
                  const budgetRows = parseBodyToChartData(budgetSource);
                  const hasMarkup = /<[a-z][^>]*>/i.test(budgetSource);
                  const showRows =
                    !hasMarkup && Object.keys(budgetRows).length > 0;

                  return (
                  <div className="col-lg-6" key={idx}>
                    <ScrollReveal delayClass={`reveal-delay-${(idx % 2) + 1}`}>
                      <div className="card border-0 shadow-sm p-3 rounded-4 bg-white h-100 hover-lift">
                        <div className="row g-3 align-items-center">
                          {budget?.banner && (
                            <div className="col-md-5">
                              <img
                                className="img-fluid rounded-3 w-100 object-fit-cover"
                                style={{ height: "140px" }}
                                src={
                                  budget?.banner.startsWith("/")
                                    ? `${rootUrl}/${budget?.banner.substr(budget?.banner.lastIndexOf("/") + 1)}`
                                    : `${rootUrl}/uploads/${budget.banner}`
                                }
                                alt={budget.title}
                              />
                            </div>
                          )}
                          <div className={budget?.banner ? "col-md-7" : "col-12"}>
                            <h6 className="fw-bold text-dark mb-2">{budget.title}</h6>
                            
                            <div
                              className="text-secondary small budget-table-wrapper"
                              style={{ maxHeight: "140px", overflowY: "auto" }}
                            >
                              <style>{`
                                .budget-table-wrapper table {
                                  width: 100%;
                                  border-collapse: separate;
                                  border-spacing: 0 4px;
                                }
                                .budget-table-wrapper th,
                                .budget-table-wrapper td {
                                  padding: 6px 10px !important;
                                  line-height: 1.4;
                                  vertical-align: middle;
                                }
                              `}</style>
                              {showRows ? (
                                <div className="d-flex flex-column gap-1">
                                  {Object.entries(budgetRows).map(([label, value]) => (
                                    <div
                                      key={label}
                                      className="d-flex justify-content-between align-items-center py-1"
                                      style={{ borderBottom: "1px solid #f0f0f0", fontSize: "13px" }}
                                    >
                                      <span className="text-secondary">{label}</span>
                                      <span
                                        className="fw-semibold text-dark"
                                        style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                                      >
                                        ₦{Number(value).toLocaleString("en-NG")}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div dangerouslySetInnerHTML={{ __html: budgetSource }} />
                              )}
                            </div>

                          </div>
                        </div>
                      </div>
                    </ScrollReveal>
                  </div>
                  );
                })}
              </div>

              <div className="text-center">
                <Link to="/BudgetPage" className="btn btn-outline-secondary px-4 py-2 fw-semibold rounded-pill">
                  View Budget Archives
                </Link>
              </div>
            </div>
          </section>
        </ScrollReveal>
      )}

      {/* 8. Partner CTA Section */}
      <ScrollReveal>
        <section className="py-5 text-white text-center" style={{ backgroundColor: "#0B1320" }}>
          <div className="container py-3">
            <h3 className="fw-bold mb-3">Share Research or Institutional Datasets</h3>
            <p className="text-white-50 mx-auto mb-4" style={{ maxWidth: "600px" }}>
              Are you an MDA, academic partner, or research institution with open state data? Partner with us today.
            </p>
            <Link
              to="/submit-dataset"
              className="btn px-4 py-2 text-white fw-bold rounded-2 shadow-sm"
              style={{ backgroundColor: "#0C9EA9" }}
            >
              Submit Dataset ↗
            </Link>
          </div>
        </section>
      </ScrollReveal>

      {/* 9. Quick Reads / Publications Section */}
      {quickReads.length > 0 && (
        <ScrollReveal>
          <section className="py-5 bg-white border-top">
            <div className="container py-2">
              <div className="d-flex flex-wrap align-items-end justify-content-between mb-4">
                <div>
                  <h6 className="text-uppercase fw-bold" style={{ color: "#0C9EA9", letterSpacing: "1.2px", fontSize: "12px" }}>
                    Insights & Research
                  </h6>
                  <h3 className="fw-bold text-dark m-0">Quick Reads</h3>
                </div>
                <Link to="/publications" className="btn btn-outline-dark btn-sm rounded-pill px-4 fw-semibold mt-2 mt-sm-0">
                  View All Publications ↗
                </Link>
              </div>

              <div className="row g-4 mb-2">
                {quickReads.map((quickRead, idx) => (
                  <div className="col-xl-4 col-md-6 d-flex" key={idx}>
                    <ScrollReveal delayClass={`reveal-delay-${(idx % 3) + 1}`}>
                      <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden d-flex flex-column bg-light hover-lift">
                        <div
                          style={{
                            height: "160px",
                            backgroundImage: `url(${rootUrl}/uploads/${quickRead?.banner})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center"
                          }}
                        />
                        <div className="card-body p-4 d-flex flex-column">
                          <h6 className="card-title fw-bold text-dark mb-2">
                            {quickRead.title}
                          </h6>
                          <div
                            className="card-text text-black small flex-grow-1 mb-3"
                            style={{ maxHeight: "80px", overflow: "hidden" }}
                            dangerouslySetInnerHTML={{
                              __html: quickRead.body.length > 150 ? `${quickRead.body.substring(0, 150)}...` : quickRead.body,
                            }}
                          />
                          <Link
                            to={`/pages/${quickRead.slug}`}
                            className="btn btn-outline-dark btn-sm rounded-2 w-100 fw-semibold mt-auto"
                          >
                            Read Article
                          </Link>
                        </div>
                      </div>
                    </ScrollReveal>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </ScrollReveal>
      )}

      {/* 10. Weather Slider Component */}
      <WeatherSlider />

      <ToastContainer closeOnClick pauseOnHover />
      <Footer />
    </div>
  );
};

export default HomePage;